import { QueryObserver, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBoardProvider } from "../contexts/board/boardContext";
import {
  useBoardCustomFields,
  useBoardPermissions,
  useTaskUsers,
} from "../data/queries/boardQueries";
import { useClients } from "../data/queries/clientQueries";
import { useStages } from "../data/queries/stageQueries";
import { useTags } from "../data/queries/tagQueries";
import { useTaskNames } from "../data/queries/taskQueries";
import { useAllTaskTemplates } from "../data/queries/taskTemplateQueries";
import { useTaskTypeByBoardId } from "../data/queries/taskTypeQueries";
import { useTaxYears } from "../data/queries/taxReturnQueries";
import { ColumnSettings } from "../types/FilterSelectorSettings";
import { useFilingStatus } from "./useFilingStatus";

export const useBulkUpdateDataHelper = () => {
  const { t } = useTranslation(["BulkUpdateTasksModal", "Shared"], {
    useSuspense: false,
  });

  const queryClient = useQueryClient();
  const { currentBoardId } = useBoardProvider();

  const permissionQuery = useBoardPermissions(currentBoardId);
  const taskUsersQuery = useTaskUsers(currentBoardId);
  const clientsQuery = useClients();
  const stagesQuery = useStages(currentBoardId);
  const tagsQuery = useTags();
  const taskTypeQueries = useTaskTypeByBoardId(currentBoardId);
  const taxYearsQueries = useTaxYears(currentBoardId);
  const taskNameQuery = useTaskNames(currentBoardId);
  const templatesQuery = useAllTaskTemplates();
  const filingStatusQuery = useFilingStatus();
  const customFieldsQuery = useBoardCustomFields(currentBoardId);

  const getColumnDataMap = (forFilter = false) => {
    /* Filters must list every user present on tasks (including inactive or
       removed users), while update editors only offer subscribed users. */
    const assigneeSource = forFilter
      ? {
          query: taskUsersQuery,
          key: ["get-board-task-users", currentBoardId],
          modifier: (data) => [{ Id: "null", FullName: t("Unassigned") }, ...data],
        }
      : {
          query: permissionQuery,
          key: ["get-board-permissions", currentBoardId],
          modifier: (data) => [
            { Id: "null", FullName: t("Unassigned") },
            ...data.Entities,
          ],
        };

    const collaboratorsSource = forFilter
      ? {
          query: taskUsersQuery,
          key: ["get-board-task-users", currentBoardId],
        }
      : {
          query: permissionQuery,
          key: ["get-board-permissions", currentBoardId],
          modifier: (data) => data.Entities,
        };

    let baseColumns = {
      [ColumnSettings.Name.field]: {
        query: taskNameQuery,
        key: ["get-all-task-names", currentBoardId],
        fields: { value: "Id", text: "Id" },
        modifier: (data) => data.map((x) => ({ Id: x })),
      },
      [ColumnSettings.Stage.field]: {
        query: stagesQuery,
        key: ["get-stages", currentBoardId],
        fields: { value: "Id", text: ColumnSettings.Stage.displayProperty },
        modifier: (data) => data.map((x) => ({ ...x, Id: x.Order })),
      },
      ["StageId"]: {
        query: stagesQuery,
        key: ["get-stages", currentBoardId],
        fields: { value: "Id", text: ColumnSettings.Stage.displayProperty },
      },
      [ColumnSettings.Assignee.field]: {
        ...assigneeSource,
        fields: { value: "Id", text: ColumnSettings.Assignee.displayProperty },
      },
      [ColumnSettings.Collaborators.field]: {
        ...collaboratorsSource,
        fields: {
          value: "Id",
          text: ColumnSettings.Collaborators.displayProperty,
        },
      },
      ["Collaborators"]: {
        ...collaboratorsSource,
        fields: {
          value: "Id",
          text: ColumnSettings.Collaborators.displayProperty,
        },
      },
      [ColumnSettings.TaxYear.field]: {
        query: taxYearsQueries,
        key: ["get-tax-years"],
        fields: { value: "Id", text: ColumnSettings.TaxYear.displayProperty },
      },
      [ColumnSettings.Client.field]: {
        query: clientsQuery,
        key: ["get-clients"],
        fields: { value: "Id", text: ColumnSettings.Client.displayProperty },
      },
      [ColumnSettings.Tags.field]: {
        query: tagsQuery,
        key: ["get-tags"],
        fields: { value: "Id", text: ColumnSettings.Tags.displayProperty },
      },
      ["Tags"]: {
        query: tagsQuery,
        key: ["get-tags"],
        fields: { value: "Id", text: ColumnSettings.Tags.displayProperty },
      },
      [ColumnSettings.TaskType.field]: {
        query: taskTypeQueries,
        fields: { value: "Id", text: ColumnSettings.TaskType.displayProperty },
        key: ["get-task-types", currentBoardId],
      },
      [ColumnSettings.Template.field]: {
        query: templatesQuery,
        fields: { value: "Id", text: ColumnSettings.Template.displayProperty },
        key: ["get-all-task-templates"],
      },
      [ColumnSettings.FilingStatus.field]: {
        get query() {
          return filingStatusQuery;
        },
        fields: {
          value: "Id",
          text: ColumnSettings.FilingStatus.displayProperty,
        },
        key: [],
      },
    };

    customFieldsQuery.data?.forEach((customField) => {
      baseColumns[customField.Name] = {
        query: { data: customField.TaskCustomFields },
        key: ["get-board-custom-fields"],
        fields: {
          value: "Value",
          text: "Value",
        },
      };
    });

    return baseColumns;
  };

  const applyColumnMod = useCallback(
    (field, data, map) =>
      map[field].modifier ? map[field].modifier(data) : data,
    []
  );

  const getDataSource = (field, forFilter = false) => {
    const columnDataMap = getColumnDataMap(forFilter);

    if (!columnDataMap[field]) {
      console.error(
        "[BulkUpdateDataHelper]: Missing data source for field: " + field
      );
      return { data: null };
    }

    if (columnDataMap[field].query?.isPending) {
      /* Just in case data hasn't loaded yet*/
      return new Promise((resolve) => {
        const observer = new QueryObserver(queryClient, {
          queryKey: columnDataMap[field].key,
        });
        observer.subscribe((result) => {
          if (!result.isPending) {
            observer.destroy();
            resolve({
              data: result.data
                ? applyColumnMod(field, result.data, columnDataMap)
                : [],
              fields: columnDataMap[field].fields,
            });
          }
        });
      });
    } else {
      const data = applyColumnMod(
        field,
        columnDataMap[field].query.data,
        columnDataMap
      );

      if (data?.then) {
        return new Promise((resolve) => {
          data.then((data2) => {
            resolve({
              data: data2,
              fields: columnDataMap[field].fields,
            });
          });
        });
      } else {
        return {
          data: data,
          fields: columnDataMap[field].fields,
        };
      }
    }
  };

  const getFilterDataSource = (field) => getDataSource(field, true);

  return {
    getColumnDataMap,
    getDataSource,
    getFilterDataSource,
  };
};
